import { Container, Loader, Pill, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useSearchParams } from 'react-router-dom';

import { WidgetType } from '@/api/rest/resources/types/widget';
import { SuspenseBoundary, TabsList, TabTrigger } from '@/components';
import { usePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { getOrganizationSummary } from '@/components/widgets/config/organizationSummary';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { usePathSet } from '@/hooks/usePathSet';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { CreateLimitedAccessOrganization } from '@/pages/shared/components/plot-group/CreateLimitedAccessOrganization';
import { CreatePlotGroup, EditPlotGroup } from '@/pages/shared/components/plot-group/CreatePlotGroup';
import { useIsLimitedAccessUser } from '@/pages/shared/hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '@/pages/shared/hooks/useIsOrganizationMember';
import { useLimitedAccessOrganization } from '@/pages/shared/hooks/useLimitedAccessOrganization';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroup } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { PlotList } from '@/pages/shared/plots/components/PlotList';
import { ProjectsMap } from '@/pages/shared/projects/components/projectsMap/ProjectsMap';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { ImpactProjectionsPage } from './pages/impact-projections/ImpactProjections.page';
import { ImpactProjectionsPage as ImpactProjectionsPageV2 } from './pages/impact-projections-v2/ImpactProjections.page';
import { LandMonitoringPage } from './pages/land-monitoring/LandMonitoring.page';

export const SustainableProductionProjectPage = () => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();

  const [selectedPlotIds, setSelectedPlotIds] = useState<string[]>([]);

  const menuItems = useMenuItems();
  const pathSet = usePathSet();

  const [searchParams] = useSearchParams();
  const plotGroupId = searchParams.get('plotGroupId');

  const plotGroup = useOrganizationPlotGroup(organizationId, plotGroupId);
  const limitedAccessOrganization = useLimitedAccessOrganization(
    organizationId,
    plotGroupId ? { plot_group_id: plotGroupId ?? '' } : undefined,
  ).data;

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetSlotsConfiguration = useActiveWidgetSlotConfiguration(
    'default_template_details',
    WidgetType.t_page,
  );

  const widgetSlotsConfiguration = customWidgetSlotsConfiguration ?? defaultWidgetConfiguration;

  const activePlotGroup = usePlotGroup();
  const activeFilters = useGetActivePlotGroup();
  const isFiltersSelected = activeFilters?.length > 0;
  const hasGroupByFilters = !isNullish(activePlotGroup?.groupBy?.labels);

  useEffect(() => {
    if (!isFiltersSelected) {
      setSelectedPlotIds([]);
    }
  }, [isFiltersSelected]);

  // TODO: MVP-CW decide with the team whether are keeping this, if so we need a new API
  // const { plots_requiring_attention: invalidPlotsCount } = usePlotAggregate().data;
  return (
    <SignedInPageLayout
      items={menuItems}
      withCustomContentContainer
      navBarProps={{
        title: (
          <span className='flex items-center gap-2'>
            {plotGroup?.name ?? t('global.analysis.detailView')}
            {limitedAccessOrganization && limitedAccessOrganization.length > 0 ? (
              <Pill className='bg-neutral-black-8' size='small'>
                {t('global.organization.plotGroup.shared')}
              </Pill>
            ) : (
              ''
            )}
          </span>
        ),
        showFilters: true,
        // canEditFilters: false,
        children:
          !isLimitedAccessUser && isOrganizationMember ? (
            <Stack direction='row' className='justify-end gap-4'>
              {!isNullish(plotGroup) && (
                <Stack direction='row' className='justify-end gap-4'>
                  <CreateLimitedAccessOrganization
                    organizationId={organizationId}
                    plotGroups={[parsePlotGroupBodyData(plotGroup)]}
                  />
                  <EditPlotGroup organizationId={organizationId} plotGroup={parsePlotGroupBodyData(plotGroup)} />
                </Stack>
              )}
              <CreatePlotGroup
                key='filtered-group'
                organizationId={organizationId}
                plotGroup={activePlotGroup}
                plotIds={selectedPlotIds}
                disabled={!isFiltersSelected || false}
              />
            </Stack>
          ) : null,
      }}
    >
      <Container gutterWidth={{ sm: 4 }} data-testid='sustainable-production-project-page'>
        <div className='mb-10'>
          <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
        </div>
        <div className='h-[60vh] animate-in fade-in' data-testid='plots-map'>
          <ProjectsMap />
        </div>

        <TabsList className='my-10 justify-start sm:justify-center rounded-2xl bg-neutral-black-4 w-full'>
          <TabTrigger path={buildPath(pathSet.details, { queryParams: { plotGroupId } })}>
            {t('shared.projects.labels.landMonitoring')}
          </TabTrigger>
          {/* TODO: MVP-CW NCA are not ready yet to remove the connection with the projects https://thelandbankinggroup.slack.com/archives/C08JVD65EQ4/p1754639466478609?thread_ts=1754638960.654399&cid=C08JVD65EQ4 */}
          {/* <TabTrigger path={buildPath(pathSet.nca, { queryParams: { plotGroupId } })}>
            {t('shared.projects.labels.naturalCapitalAccount')}
          </TabTrigger> */}
          <TabTrigger path={buildPath(pathSet.plotGroupPlots, { queryParams: { plotGroupId } })}>
            <Stack direction='row' spacing={2} center>
              {t('shared.projects.labels.plots')}
              {/* {invalidPlotsCount > 0 && (
                <span
                  data-testid='invalid-plots-count'
                  className='typography-body2Semibold flex aspect-auto items-center justify-center rounded-full bg-warning px-2 text-primary-100'
                >
                  {invalidPlotsCount}
                </span>
              )} */}
            </Stack>
          </TabTrigger>
          {/* TODO: TEMP FIX - Remove commented TabTrigger when Imapct Projections is fixed */}
          {/* <TabTrigger path={buildPath(pathSet.impactProjections, { queryParams: { plotGroupId } })}>
            {t('shared.projects.labels.impactProjections')}
            </TabTrigger> */}
        </TabsList>
        <SuspenseBoundary
          fallback={
            <Stack center className='h-[600px]'>
              <Loader />
            </Stack>
          }
        >
          <Routes>
            <Route index element={<LandMonitoringPage />} />
            {/* TODO: MVP-CW NCA are not ready yet to remove the connection with the projects https://thelandbankinggroup.slack.com/archives/C08JVD65EQ4/p1754639466478609?thread_ts=1754638960.654399&cid=C08JVD65EQ4 */}
            {/* <Route path='nca' element={<NCAPage />} /> */}
            <Route
              path='plots'
              element={
                hasGroupByFilters ? (
                  <PlotList />
                ) : (
                  <PlotList
                    enableRowSelection={true}
                    defaultRowSelected={false}
                    setSelectedPlotIds={setSelectedPlotIds}
                    selectedPlotIds={selectedPlotIds}
                  />
                )
              }
            />
            <Route path='impact-projections' element={<ImpactProjectionsPage />} />
            <Route path='impact-projections-v2' element={<ImpactProjectionsPageV2 />} />
          </Routes>
        </SuspenseBoundary>
      </Container>
    </SignedInPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  return {
    name: 'default_template_details',
    type: WidgetType.t_page,
    widgets: [getOrganizationSummary()],
  };
};
